import { useState, type FormEvent } from 'react';

import { Avatar, Button, Field, Input, Textarea } from '@/design-system';
import { publicReportProblemOf, type PublicInspector } from '@/entities/publicReport';
import { initialsFor } from '@/entities/user';

import type { PublicCopy } from '../copy';
import { looksLikeEmail } from '../format';
import { ActionLink } from './ActionLink';
import { PublicBlock } from './PublicBlock';
import { SectionTitle } from './SectionTitle';

export interface InspectorCardProps {
    inspector: PublicInspector;
    copy: PublicCopy;
    headingLevel: 2 | 3;
    onAsk: (message: string, email: string | null) => Promise<void>;
    /**
     * Alleen in de admin-preview: het formulier is te zien zoals de koper het
     * ziet, maar versturen kan niet - deze regel zegt waarom.
     */
    previewNote?: string;
}

/** De API wil minstens zoveel tekens in een vraag. */
const MIN_QUESTION_LENGTH = 3;

/** En hoogstens zoveel. */
const MAX_QUESTION_LENGTH = 2000;

/**
 * De koper praat met zijn *eigen* inspecteur, niet met een helpdesk. De vraag
 * opent inline - dit ontwerp kent geen blokkerende modal. Een e-mailadres voor
 * het antwoord mag, maar hoeft niet.
 */
export function InspectorCard({
    inspector,
    copy,
    headingLevel,
    onAsk,
    previewNote,
}: InspectorCardProps) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [messageError, setMessageError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);

    const meta = [inspector.qualification, inspector.responseTime].filter(Boolean).join(' · ');
    const subtitle = meta ? `${copy.inspectorRole} · ${meta}` : copy.inspectorRole;

    async function submit(event: FormEvent) {
        event.preventDefault();

        if (previewNote) {
            return;
        }

        const question = message.trim();
        const replyTo = email.trim();

        setMessageError(question.length < MIN_QUESTION_LENGTH ? copy.askTooShort : null);
        setEmailError(replyTo !== '' && !looksLikeEmail(replyTo) ? copy.askEmailInvalid : null);

        if (question.length < MIN_QUESTION_LENGTH || (replyTo !== '' && !looksLikeEmail(replyTo))) {
            return;
        }

        setBusy(true);

        try {
            await onAsk(question, replyTo === '' ? null : replyTo);
            setSent(true);
            setOpen(false);
            setMessage('');
            setEmail('');
        } catch (failure) {
            const kind = publicReportProblemOf(failure).kind;

            setMessageError(
                kind === 'tooManyAttempts'
                    ? copy.askTooMany
                    : kind === 'offline'
                      ? copy.problemBodies.offline
                      : copy.actionFailed,
            );
        } finally {
            setBusy(false);
        }
    }

    return (
        <PublicBlock>
            <SectionTitle level={headingLevel}>{copy.questionsTitle}</SectionTitle>

            <div className="flex flex-wrap items-center gap-[14px] rounded-[10px] border border-[var(--border-default)] p-4">
                {inspector.avatarUrl ? (
                    <img
                        src={inspector.avatarUrl}
                        alt=""
                        className="h-[54px] w-[54px] shrink-0 rounded-full border border-[var(--placeholder-border)] object-cover"
                    />
                ) : (
                    <Avatar size={54}>{initialsFor(inspector.name)}</Avatar>
                )}

                <div className="min-w-0 flex-[1_1_200px]">
                    <div className="text-[15px] leading-[1.3] font-medium text-[var(--text-primary)]">
                        {inspector.name}
                    </div>
                    <div className="mt-[3px] text-[13px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                        {subtitle}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="dark"
                        size="md"
                        aria-expanded={open}
                        onClick={() => {
                            setOpen((value) => !value);
                            setSent(false);
                        }}
                    >
                        {copy.askOpen}
                    </Button>
                    {inspector.phone ? (
                        <ActionLink
                            href={`tel:${inspector.phone.replace(/\s/g, '')}`}
                            variant="outline"
                            size="md"
                        >
                            {copy.callLabel}
                        </ActionLink>
                    ) : null}
                </div>
            </div>

            {open ? (
                <form onSubmit={submit} className="mt-3 flex flex-col gap-3" noValidate>
                    <Field label={copy.askLabel} size="lg" error={messageError ?? undefined}>
                        <Textarea
                            value={message}
                            rows={4}
                            maxLength={MAX_QUESTION_LENGTH}
                            placeholder={copy.askPlaceholder}
                            onChange={(event) => setMessage(event.target.value)}
                        />
                    </Field>
                    <Field label={copy.askEmailLabel} size="lg" error={emailError ?? undefined}>
                        <Input
                            type="email"
                            size="lg"
                            inputMode="email"
                            autoComplete="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                        />
                    </Field>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={busy || previewNote !== undefined}
                        >
                            {copy.askSubmit}
                        </Button>
                        <Button variant="outline" size="lg" onClick={() => setOpen(false)}>
                            {copy.askCancel}
                        </Button>
                    </div>
                    {previewNote ? (
                        <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                            {previewNote}
                        </p>
                    ) : null}
                </form>
            ) : null}

            {sent ? (
                <p
                    role="status"
                    className="mt-3 mb-0 text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {copy.askSent}
                </p>
            ) : null}
        </PublicBlock>
    );
}
